/**
 * SCWT-PM-V2 Phase 7 · Slice E — Admin Water Health Program panel.
 *
 * Route: /admin/water-health (protected by ProtectedRoute + admin role).
 *
 * Approved Slice-E actions ONLY:
 *   • search / filter / view
 *   • reconcile with Stripe
 *   • suspend  (requires reason, confirmation)
 *   • resume   (requires reason, confirmation)
 *   • change eligible member-discount % (0–30)
 *   • verify configured Stripe Price
 *
 * Payer transfer + exceptional refunds are DEFERRED — no placeholder
 * UI to prevent confusion.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Droplet, RefreshCw, Search, Shield, Pause, Play,
  Settings2, CheckCircle2, AlertCircle, Eye,
} from "lucide-react";
import { LWH_ADMIN_STATUS_LABEL, centsToAud, fmtDateAU } from "@/lib/lwhStatus";
import { useFeature } from "@/lib/useFeature";
import LwhStatsTile from "@/components/admin/LwhStatsTile";

const base = process.env.REACT_APP_BACKEND_URL;
function adminApi(token) {
  return axios.create({
    baseURL: `${base}/api`,
    headers: { Authorization: `Bearer ${token}` },
  });
}

export default function AdminWaterHealth({ token }) {
  const [q, setQ]           = useState("");
  const [statusFilter, setStatus] = useState("");
  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [selected, setSel]  = useState(null);
  const [error, setError]   = useState(null);
  const [config, setConfig] = useState(null);

  const api = useMemo(() => adminApi(token), [token]);
  const { isVisible: lwhVisible } = useFeature("landlord_water_health_program");

  const load = async () => {
    setLoad(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusFilter) params.set("status", statusFilter);
      const { data } = await api.get(`/admin/water-health/memberships?${params}`);
      setItems(data.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load memberships.");
    } finally { setLoad(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="admin-water-health-page" className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        <header className="flex items-start justify-between gap-3 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-700">
              <Droplet className="w-3.5 h-3.5" /> SCWT-PM-V2 · Phase 7
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-1">
              Water Health Program admin
            </h1>
            <p className="mt-1.5 text-sm text-slate-600 max-w-2xl">
              Search, reconcile, suspend and resume landlord memberships.
              Payer transfer and exceptional refunds are handled outside this
              panel for now.
            </p>
          </div>
        </header>

        {lwhVisible && (
          <LwhStatsTile
            api={api}
            onFilterStatus={(k, extras) => {
              setStatus(k);
              // Trigger reload with the new status filter + optional
              // reconciliation_age refinement from the sub-indicator.
              (async () => {
                setLoad(true); setError(null);
                try {
                  const params = new URLSearchParams();
                  if (q) params.set("q", q);
                  if (k) params.set("status", k);
                  if (extras?.reconciliation_age) {
                    params.set("reconciliation_age", extras.reconciliation_age);
                  }
                  const { data } = await api.get(`/admin/water-health/memberships?${params}`);
                  setItems(data.items || []);
                } catch (e) {
                  setError(e?.response?.data?.detail || "Could not load memberships.");
                } finally { setLoad(false); }
              })();
              // Scroll to results so the user sees the filter apply
              try {
                document.querySelector('[data-testid="admin-water-health-table"]')
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              } catch { /* noop */ }
            }}
          />
        )}

        <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
            <input
              data-testid="admin-water-health-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search reference / property / landlord / stripe sub"
              className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-300 text-sm"
            />
          </div>
          <select
            data-testid="admin-water-health-status-filter"
            value={statusFilter}
            onChange={(e) => setStatus(e.target.value)}
            className="h-10 px-3 rounded-xl border border-slate-300 text-sm"
          >
            <option value="">All statuses</option>
            {Object.entries(LWH_ADMIN_STATUS_LABEL).map(([k, v]) =>
              <option key={k} value={k}>{v}</option>,
            )}
          </select>
          <button
            data-testid="admin-water-health-search-btn"
            onClick={load}
            className="h-10 px-4 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold"
          >Search</button>
          <button
            data-testid="admin-water-health-config-btn"
            onClick={() => setConfig({ open: true })}
            className="h-10 px-3 rounded-xl border border-slate-300 hover:border-cyan-300 text-slate-700 text-sm font-semibold inline-flex items-center gap-1.5"
          ><Settings2 className="w-4 h-4" /> Config</button>
        </div>

        {loading && <div className="text-sm text-slate-500 py-10 text-center">Loading…</div>}
        {error && <div data-testid="admin-water-health-error"
                        className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{error}</div>}

        {!loading && !error && (
          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Reference</th>
                  <th className="text-left px-4 py-2.5">Property</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">Payer</th>
                  <th className="text-left px-4 py-2.5">Renews</th>
                  <th className="text-right px-4 py-2.5"> </th>
                </tr>
              </thead>
              <tbody data-testid="admin-water-health-table">
                {items.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs">{m.reference}</td>
                    <td className="px-4 py-3 text-slate-700 max-w-xs truncate">{m.property_id}</td>
                    <td className="px-4 py-3">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                        {LWH_ADMIN_STATUS_LABEL[m.status] || m.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 capitalize">{(m.payer_type || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-slate-700">{fmtDateAU(m.current_period_end)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`admin-water-health-open-${m.id}`}
                        onClick={() => setSel(m)}
                        className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900 text-xs font-semibold"
                      ><Eye className="w-3.5 h-3.5" /> Open</button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-500">No memberships found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <AdminDrawer membership={selected} api={api}
                      onClose={() => setSel(null)}
                      onChanged={() => { setSel(null); load(); }} />
      )}
      {config?.open && (
        <AdminConfigDrawer api={api} onClose={() => setConfig(null)} />
      )}
    </div>
  );
}

function AdminDrawer({ membership: initial, api, onClose, onChanged }) {
  const [m, setM]    = useState(initial);
  const [busy, setB] = useState(false);
  const [action, setAction] = useState(null); // "suspend" | "resume" | null
  const [reason, setReason] = useState("");
  const [err, setErr]   = useState(null);
  const [note, setNote] = useState(null);

  const run = async (kind) => {
    if (kind === "suspend" && !reason.trim()) { setErr("Reason is required."); return; }
    if (kind === "resume"  && !reason.trim()) { setErr("Reason is required."); return; }
    setB(true); setErr(null);
    try {
      let r;
      if (kind === "suspend") {
        r = await api.post(`/admin/water-health/${m.id}/suspend`, { reason });
      } else if (kind === "resume") {
        r = await api.post(`/admin/water-health/${m.id}/resume`);
      } else if (kind === "reconcile") {
        r = await api.post(`/admin/water-health/${m.id}/reconcile`);
      }
      setM(r.data);
      setNote(`${kind} succeeded`);
      setAction(null); setReason("");
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail?.detail || e?.response?.data?.detail || "Action failed.");
    } finally { setB(false); }
  };

  return (
    <div data-testid="admin-water-health-drawer"
          className="fixed inset-0 z-50 flex justify-end bg-slate-900/50" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 z-10 bg-white border-b px-5 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono text-xs text-slate-500">{m.reference}</div>
            <h3 className="font-display text-lg font-bold text-slate-900">{LWH_ADMIN_STATUS_LABEL[m.status] || m.status}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="admin-water-health-drawer-close">✕</button>
        </header>
        <div className="p-5 space-y-4">
          {note && <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">{note}</div>}
          {err && <div data-testid="admin-water-health-drawer-error"
                        className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800">{err}</div>}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Property" value={m.property_id} />
            <Field label="Landlord" value={m.landlord_id} />
            <Field label="Payer" value={(m.payer_type || "").replace(/_/g, " ")} />
            <Field label="Enrolment source" value={(m.enrolment_source || "").replace(/_/g, " ")} />
            <Field label="Period start" value={fmtDateAU(m.current_period_start)} />
            <Field label="Period end" value={fmtDateAU(m.current_period_end)} />
            <Field label="Stripe sub" value={m.stripe_subscription_id || "—"} mono />
            <Field label="Stripe customer" value={m.stripe_customer_id || "—"} mono />
            <Field label="Cancel at period end" value={m.cancel_at_period_end ? "yes" : "no"} />
            <Field label="Suspend reason" value={m.suspend_reason || "—"} />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <button data-testid="admin-water-health-reconcile"
                    onClick={() => run("reconcile")} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 hover:border-cyan-300 text-slate-700 hover:text-cyan-800 text-sm font-semibold px-3 py-2 disabled:opacity-40">
              <RefreshCw className="w-3.5 h-3.5" /> Reconcile with Stripe
            </button>
            {m.status !== "suspended" && (
              <button data-testid="admin-water-health-suspend-btn"
                      onClick={() => { setAction("suspend"); setErr(null); }}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-rose-300 text-rose-700 hover:bg-rose-50 text-sm font-semibold px-3 py-2">
                <Pause className="w-3.5 h-3.5" /> Suspend
              </button>
            )}
            {m.status === "suspended" && (
              <button data-testid="admin-water-health-resume-btn"
                      onClick={() => { setAction("resume"); setErr(null); }}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold px-3 py-2">
                <Play className="w-3.5 h-3.5" /> Resume
              </button>
            )}
          </div>

          {action && (
            <div data-testid={`admin-water-health-${action}-form`}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div className="text-sm font-semibold text-slate-900 capitalize">Confirm {action}</div>
              <textarea
                data-testid={`admin-water-health-${action}-reason`}
                value={reason} onChange={(e) => setReason(e.target.value)}
                rows={3} maxLength={500}
                placeholder="Enter a reason (required — stored in audit log)"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setAction(null); setReason(""); setErr(null); }}
                        className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
                <button data-testid={`admin-water-health-${action}-confirm`}
                        onClick={() => run(action)} disabled={busy || !reason.trim()}
                        className="rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white text-sm font-semibold px-3 py-1.5">
                  Confirm {action}
                </button>
              </div>
            </div>
          )}

          {Array.isArray(m.audit_log) && m.audit_log.length > 0 && (
            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Audit history</div>
              <ol className="space-y-1.5 text-xs" data-testid="admin-water-health-audit-log">
                {[...m.audit_log].reverse().slice(0, 20).map((e, i) => (
                  <li key={i} className="rounded-lg bg-slate-50 border border-slate-200 p-2">
                    <span className="font-semibold text-slate-700">{e.action}</span>
                    <span className="text-slate-500"> · {fmtDateAU(e.at)} · {e.actor}</span>
                    {e.detail && <div className="mt-0.5 text-slate-600">{JSON.stringify(e.detail)}</div>}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminConfigDrawer({ api, onClose }) {
  const [cfg, setCfg]   = useState(null);
  const [pct, setPct]   = useState(10);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState(null);
  const [ver, setVer]   = useState(null);

  useEffect(() => {
    api.get("/admin/feature-flags").catch(() => {}); // warm-up
    // Fetch current config via the discount POST as a preview isn't
    // exposed — instead we simply show the current pct from the
    // membership list eventually; for now the field is editable.
  }, [api]);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/admin/water-health/config/discount",
        { member_discount_pct: Number(pct) });
      setCfg(data); setMsg("Saved.");
    } catch (e) {
      setMsg(e?.response?.data?.detail?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setVer(null);
    try {
      const { data } = await api.post("/admin/water-health/config/verify-price");
      setVer(data);
    } catch (e) {
      setVer({ ok: false, issues: [e?.response?.data?.detail || "verify failed"] });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50" onClick={onClose}
          data-testid="admin-water-health-config-drawer">
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 z-10 bg-white border-b px-5 py-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-slate-900">Program configuration</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"
                    data-testid="admin-water-health-config-close">✕</button>
        </header>
        <div className="p-5 space-y-5">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
              Member discount % (0–30)
            </label>
            <input type="number" min={0} max={30}
                    data-testid="admin-water-health-config-pct"
                    value={pct} onChange={(e) => setPct(e.target.value)}
                    className="mt-1.5 w-full h-11 rounded-xl border border-slate-300 px-3 text-sm"/>
            <p className="mt-1 text-[11px] text-slate-500">
              Applies to eligible retail lab tests booked for enrolled properties. Existing
              memberships keep the pct that was frozen at their period start.
            </p>
            <button data-testid="admin-water-health-config-save"
                    onClick={save} disabled={busy}
                    className="mt-3 inline-flex rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white text-sm font-semibold px-4 py-2">
              {busy ? "Saving…" : "Save discount %"}
            </button>
          </div>

          <div className="pt-4 border-t border-slate-200">
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-1.5">Verify Stripe Price</div>
            <button data-testid="admin-water-health-verify-price"
                    onClick={verify} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 hover:border-cyan-300 text-slate-700 text-sm font-semibold px-3 py-2">
              <Shield className="w-3.5 h-3.5" /> Verify Stripe Price
            </button>
            {ver && (
              <div className={`mt-3 rounded-xl border p-3 text-xs ${
                ver.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                        : "bg-rose-50 border-rose-200 text-rose-800"}`}>
                {ver.ok
                  ? <><CheckCircle2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Price matches config.</>
                  : <><AlertCircle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> {(ver.issues || []).join("; ") || "Verification failed."}</>}
              </div>
            )}
          </div>

          {msg && <div className="rounded-xl bg-cyan-50 border border-cyan-200 p-3 text-xs text-cyan-800">{msg}</div>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</div>
      <div className={`mt-0.5 text-slate-800 ${mono ? "font-mono text-xs" : "text-sm"} break-all`}>
        {value || "—"}
      </div>
    </div>
  );
}
